import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import { useToast } from "@/components/application/notifications/toast-provider";
import { apiCall, ApiError } from "../../lib/apiClient";

/**
 * Registr držitelů (Holder) — právnické (PO) a fyzické (FO) osoby.
 * PO nese IČO/DIČ/adresu (daňový doklad UC-008); FO je napojená na app uživatele.
 */

interface Holder {
  id: string;
  tenant_id: string;
  holder_type: "legal" | "physical";
  display_name: string;
  user_id: string | null;
  ico: string | null;
  dic: string | null;
  address: string | null;
  deactivated_at: string | null;
}

interface TenantRow {
  id: string;
  display_name: string;
  tenant_type: string;
}

interface AdminUser {
  user_id: string;
  display_name: string;
  email: string;
}

function HoldersPage() {
  const [holders, setHolders] = useState<Holder[]>([]);
  const [tenantOptions, setTenantOptions] = useState<{ tenant_id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Holder | null>(null);
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [hl, tn] = await Promise.all([
        apiCall<{ rows: Holder[] }>("/api/admin/holders"),
        apiCall<{ rows: TenantRow[] }>("/api/admin/tenants"),
      ]);
      setHolders(hl.rows);
      setTenantOptions(
        tn.rows.map((t) => ({ tenant_id: t.id, label: t.display_name })),
      );
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení držitelů selhalo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const deactivate = useCallback(
    async (h: Holder) => {
      try {
        await apiCall(`/api/admin/holders/${h.id}`, { method: "DELETE" });
        toast.push({ variant: "success", message: `Držitel „${h.display_name}" deaktivován.` });
        await fetchAll();
      } catch (e) {
        const msg =
          e instanceof ApiError && e.errorCode === "HOLDER_IN_USE"
            ? "Držitele nelze deaktivovat — je přiřazen kartám nebo je default holder střediska."
            : e instanceof Error
              ? e.message
              : "Deaktivace selhala.";
        toast.push({ variant: "error", message: msg });
      }
    },
    [fetchAll, toast],
  );

  const columns = useMemo<Column<Holder>[]>(
    () => [
      {
        key: "name",
        header: "Název",
        cell: (r) => <strong className="text-primary">{r.display_name}</strong>,
      },
      {
        key: "type",
        header: "Typ",
        cell: (r) => (
          <Badge color={r.holder_type === "legal" ? "blue" : "gray"}>
            {r.holder_type === "legal" ? "Právnická osoba" : "Fyzická osoba"}
          </Badge>
        ),
      },
      {
        key: "detail",
        header: "Identifikace",
        cell: (r) =>
          r.holder_type === "legal" ? (
            <span className="text-sm text-tertiary">
              {r.ico ? `IČO ${r.ico}` : "—"}
              {r.dic ? ` · DIČ ${r.dic}` : ""}
            </span>
          ) : (
            <span className="text-sm text-tertiary">app uživatel</span>
          ),
      },
      {
        key: "status",
        header: "Stav",
        cell: (r) =>
          r.deactivated_at ? (
            <Badge color="warning">Deaktivován</Badge>
          ) : (
            <BadgeWithDot color="success">
              Aktivní
            </BadgeWithDot>
          ),
      },
      {
        key: "actions",
        header: "Akce",
        cell: (r) => (
          <div className="flex gap-2">
            <Button size="sm" color="secondary" onPress={() => setEditTarget(r)}>
              Upravit
            </Button>
            {!r.deactivated_at && (
              <Button size="sm" color="secondary" onPress={() => void deactivate(r)}>
                Deaktivovat
              </Button>
            )}
          </div>
        ),
      },
    ],
    [deactivate],
  );

  return (
    <section aria-labelledby="holders-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="holders-title" className="text-2xl font-bold text-primary m-0">
            Držitelé
          </h2>
          <p className="text-base text-tertiary m-0">
            Registr držitelů nákladu — právnické (firmy) a fyzické osoby.
          </p>
        </div>
        <Button color="primary" onPress={() => setCreateOpen(true)} data-testid="holder-add">
          Přidat držitele
        </Button>
      </header>

      {loadError && <InlineAlert variant="error">{loadError}</InlineAlert>}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Seznam držitelů
            {!loading && <span className="ml-2 text-sm font-normal text-tertiary">({holders.length})</span>}
          </h3>
        </CardHeader>
        <CardBody>
          {loading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable
              caption="Registr držitelů"
              columns={columns}
              rows={holders}
              rowKey={(r) => r.id}
              emptyMessage="Zatím žádní držitelé. Přidej prvního tlačítkem vpravo nahoře."
            />
          )}
        </CardBody>
      </Card>

      {createOpen && (
        <HolderModal
          tenantOptions={tenantOptions}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void fetchAll();
          }}
        />
      )}
      {editTarget && (
        <HolderModal
          tenantOptions={tenantOptions}
          existing={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void fetchAll();
          }}
        />
      )}
    </section>
  );
}

interface HolderModalProps {
  tenantOptions: { tenant_id: string; label: string }[];
  existing?: Holder;
  onClose: () => void;
  onSaved: () => void;
}

function HolderModal({ tenantOptions, existing, onClose, onSaved }: HolderModalProps) {
  const isEdit = existing != null;
  // Single-tenant deployment: tenant se nevybírá v UI, přiřadí se automaticky
  // (jediný tenant z tenantOptions; u editace zůstává existující).
  const [tenantId] = useState(existing?.tenant_id ?? tenantOptions[0]?.tenant_id ?? "");
  const [type, setType] = useState<"legal" | "physical">(existing?.holder_type ?? "legal");
  const [displayName, setDisplayName] = useState(existing?.display_name ?? "");
  const [userId, setUserId] = useState(existing?.user_id ?? "");
  const [ico, setIco] = useState(existing?.ico ?? "");
  const [dic, setDic] = useState(existing?.dic ?? "");
  const [address, setAddress] = useState(existing?.address ?? "");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  // Načti uživatele tenantu pro výběr (FO).
  useEffect(() => {
    if (type !== "physical" || !tenantId) return;
    let active = true;
    void apiCall<{ users: AdminUser[] }>(`/api/admin/users?tenant_id=${tenantId}`)
      .then((r) => {
        if (active) setUsers(r.users);
      })
      .catch(() => {
        if (active) setUsers([]);
      });
    return () => {
      active = false;
    };
  }, [type, tenantId]);

  async function submit() {
    if (displayName.trim().length === 0) {
      setFormError("Název je povinný.");
      return;
    }
    if (type === "physical" && userId === "") {
      setFormError("Fyzická osoba musí mít napojeného uživatele.");
      return;
    }
    if (type === "legal" && ico.trim().length === 0) {
      setFormError("Právnická osoba musí mít IČO.");
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      if (isEdit) {
        await apiCall(`/api/admin/holders/${existing.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            display_name: displayName.trim(),
            user_id: type === "physical" ? userId : null,
            ico: type === "legal" ? ico.trim() : null,
            dic: type === "legal" ? dic.trim() : null,
            address: type === "legal" ? address.trim() : null,
          }),
        });
      } else {
        await apiCall("/api/admin/holders", {
          method: "POST",
          body: JSON.stringify({
            tenant_id: tenantId,
            holder_type: type,
            display_name: displayName.trim(),
            user_id: type === "physical" ? userId : null,
            ico: type === "legal" ? ico.trim() : null,
            dic: type === "legal" ? dic.trim() : null,
            address: type === "legal" ? address.trim() : null,
          }),
        });
      }
      toast.push({ variant: "success", message: `Držitel „${displayName.trim()}" uložen.` });
      onSaved();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Uložení selhalo.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      title={isEdit ? `Upravit držitele — ${existing.display_name}` : "Přidat držitele"}
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={submitting} isLoading={submitting} data-testid="holder-save">
            {isEdit ? "Uložit" : "Vytvořit"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <InlineAlert variant="error">{formError}</InlineAlert>}

        {!isEdit && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="holder-type-select" className="text-sm font-medium text-secondary">
              Typ držitele <span className="text-brand-tertiary">*</span>
            </label>
            <select
              id="holder-type-select"
              className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              value={type}
              onChange={(e) => setType(e.target.value as "legal" | "physical")}
              disabled={submitting}
              data-testid="holder-type"
            >
              <option value="legal">Právnická osoba (firma)</option>
              <option value="physical">Fyzická osoba</option>
            </select>
          </div>
        )}

        <Input
          label="Název"
          isRequired
          hint={type === "legal" ? "Obchodní název firmy." : "Jméno osoby."}
          value={displayName}
          onChange={setDisplayName}
          isDisabled={submitting}
          data-testid="holder-name"
        />

        {type === "physical" && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="holder-user-select" className="text-sm font-medium text-secondary">
              Napojený uživatel <span className="text-brand-tertiary">*</span>
            </label>
            <select
              id="holder-user-select"
              className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={submitting}
              data-testid="holder-user"
            >
              <option value="">— vyber uživatele —</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.display_name} ({u.email})
                </option>
              ))}
            </select>
            <p className="text-sm text-tertiary">Fyzická osoba musí mít app uživatele (souhlas + QR úhrada).</p>
          </div>
        )}

        {type === "legal" && (
          <>
            <Input label="IČO" isRequired value={ico} onChange={setIco} isDisabled={submitting} data-testid="holder-ico" />
            <Input label="DIČ (volitelně)" value={dic} onChange={setDic} isDisabled={submitting} data-testid="holder-dic" />
            <Input label="Adresa (volitelně)" value={address} onChange={setAddress} isDisabled={submitting} data-testid="holder-address" />
          </>
        )}
      </div>
    </Modal>
  );
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/holders",
  component: HoldersPage,
});
